// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Sales Status Filter Dropdown Component
// ─────────────────────────────────────────────────────────────────────────────
// Replaces cluttered walls of status buttons with an elegant, responsive,
// multi-select filter dropdown for mobile and desktop views.
// Includes quick presets and category-grouped operational statuses.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  OPERATIONAL_STATUSES,
  COMPOUND_PRESETS,
  CompoundPreset,
} from '@/utils/statusUtils';
import {
  Filter,
  Check,
  ChevronDown,
  Sparkles,
  Package,
  CreditCard,
  RotateCcw,
} from 'lucide-react';

interface SalesStatusFilterDropdownProps {
  selectedStatuses: string[];
  onToggleStatus: (statusId: string) => void;
  activePreset: string | null;
  onSelectPreset: (preset: CompoundPreset) => void;
  onClearFilters: () => void;
  statusCounts: Record<string, number>;
  presetCounts: Record<string, number>;
  totalCount: number;
}

export default function SalesStatusFilterDropdown({
  selectedStatuses,
  onToggleStatus,
  activePreset,
  onSelectPreset,
  onClearFilters,
  statusCounts,
  presetCounts,
  totalCount,
}: SalesStatusFilterDropdownProps) {
  const [open, setOpen] = useState(false);

  const hasActiveFilters = selectedStatuses.length > 0 || !!activePreset;

  // Compute trigger button label
  const triggerLabel = (() => {
    if (activePreset) {
      const preset = COMPOUND_PRESETS.find((p) => p.id === activePreset);
      return `Preset: ${preset?.label ?? 'Custom'}`;
    }
    if (selectedStatuses.length === 0) {
      return `Status: All (${totalCount})`;
    }
    if (selectedStatuses.length === 1) {
      const firstId = selectedStatuses[0] ?? '';
      const op = OPERATIONAL_STATUSES.find((s) => s.id === firstId);
      const count = firstId ? (statusCounts[firstId] ?? 0) : 0;
      return `${op?.label ?? '1 Status'} (${count})`;
    }
    return `${selectedStatuses.length} Statuses Selected`;
  })();

  const fulfillmentStatuses = OPERATIONAL_STATUSES.filter((s) => s.category === 'fulfillment');
  const paymentStatuses = OPERATIONAL_STATUSES.filter((s) => s.category === 'payment');
  const returnStatuses = OPERATIONAL_STATUSES.filter((s) => s.category === 'return_refund');

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
            hasActiveFilters
              ? 'border-primary bg-primary/10 text-primary font-semibold ring-1 ring-primary/40 shadow-2xs'
              : 'border-border bg-card text-foreground hover:bg-accent hover:border-primary/40'
          }`}
          aria-label="Filter orders by status"
          title="Filter orders by operational status"
        >
          <Filter className={`h-3.5 w-3.5 ${hasActiveFilters ? 'text-primary' : 'text-muted-foreground'}`} />
          <span className="truncate max-w-[180px] sm:max-w-xs">{triggerLabel}</span>
          {hasActiveFilters && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground font-mono">
              {selectedStatuses.length || 1}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-auto shrink-0" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={6}
          avoidCollisions={true}
          collisionPadding={12}
          className="z-50 w-[95vw] sm:w-[420px] rounded-xl border border-border bg-card p-0 text-card-foreground shadow-2xl outline-none focus-visible:outline-none max-h-[82vh] flex flex-col animate-in fade-in-0 zoom-in-95 overflow-hidden"
        >
          {/* Popover Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5 bg-muted/30 shrink-0">
            <div>
              <h4 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-primary" /> Filter by Status
              </h4>
              <p className="text-[11px] text-muted-foreground">
                Select 1 or more statuses across retail dimensions
              </p>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Scrollable Status Options Body */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3.5 min-h-0">
            {/* 1. Quick Presets Section */}
            <div className="space-y-1.5">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-amber-500" /> Quick Multi-Status Views
              </span>
              <div className="flex flex-wrap gap-1.5">
                {COMPOUND_PRESETS.map((preset) => {
                  const isActive = activePreset === preset.id;
                  const count = presetCounts[preset.id] ?? 0;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => onSelectPreset(preset)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-all ${
                        isActive
                          ? 'border-primary bg-primary/15 text-primary font-semibold shadow-2xs'
                          : 'border-border bg-background text-foreground/80 hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {isActive && <Check className="h-3 w-3 text-primary stroke-[3]" />}
                      <span>{preset.label}</span>
                      <span className={`rounded-full px-1 text-[9px] font-mono ${
                        isActive ? 'bg-primary/20 text-primary font-bold' : 'bg-muted text-muted-foreground'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="h-px bg-border/60" />

            {/* 2. Operational Categories */}
            <div className="space-y-3">
              {/* Category A: Fulfillment */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider pb-0.5">
                  <span className="flex items-center gap-1.5">
                    <Package className="h-3 w-3 text-blue-500" /> 1. Fulfillment Status
                  </span>
                  <span>1 - 3</span>
                </div>
                <div className="space-y-1">
                  {fulfillmentStatuses.map((op) => {
                    const isChecked = selectedStatuses.includes(op.id);
                    const count = statusCounts[op.id] ?? 0;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => onToggleStatus(op.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border text-left text-xs transition-colors ${
                          isChecked
                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                            : 'border-border/60 bg-background/80 hover:bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            isChecked
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-muted-foreground/40 bg-background'
                          }`}>
                            {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                          </div>
                          <span className="truncate">{op.label}</span>
                        </div>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono ${
                          isChecked ? 'bg-primary/20 text-primary font-bold' : 'bg-muted text-muted-foreground'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category B: Payment */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider pb-0.5">
                  <span className="flex items-center gap-1.5">
                    <CreditCard className="h-3 w-3 text-emerald-500" /> 2. Payment Status
                  </span>
                  <span>4 - 5</span>
                </div>
                <div className="space-y-1">
                  {paymentStatuses.map((op) => {
                    const isChecked = selectedStatuses.includes(op.id);
                    const count = statusCounts[op.id] ?? 0;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => onToggleStatus(op.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border text-left text-xs transition-colors ${
                          isChecked
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold'
                            : 'border-border/60 bg-background/80 hover:bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            isChecked
                              ? 'border-emerald-500 bg-emerald-500 text-white'
                              : 'border-muted-foreground/40 bg-background'
                          }`}>
                            {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                          </div>
                          <span className="truncate">{op.label}</span>
                        </div>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono ${
                          isChecked ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold' : 'bg-muted text-muted-foreground'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category C: Return & Refund */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[11px] font-bold text-muted-foreground uppercase tracking-wider pb-0.5">
                  <span className="flex items-center gap-1.5">
                    <RotateCcw className="h-3 w-3 text-purple-500" /> 3. Return & Refund Status
                  </span>
                  <span>6 - 10</span>
                </div>
                <div className="space-y-1">
                  {returnStatuses.map((op) => {
                    const isChecked = selectedStatuses.includes(op.id);
                    const count = statusCounts[op.id] ?? 0;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => onToggleStatus(op.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border text-left text-xs transition-colors ${
                          isChecked
                            ? 'border-purple-500 bg-purple-500/10 text-purple-700 dark:text-purple-300 font-semibold'
                            : 'border-border/60 bg-background/80 hover:bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            isChecked
                              ? 'border-purple-500 bg-purple-500 text-white'
                              : 'border-muted-foreground/40 bg-background'
                          }`}>
                            {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                          </div>
                          <span className="truncate">{op.label}</span>
                        </div>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono ${
                          isChecked ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 font-bold' : 'bg-muted text-muted-foreground'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Popover Footer */}
          <div className="border-t border-border bg-card px-4 py-2.5 flex items-center justify-between shrink-0">
            <span className="text-[11px] text-muted-foreground font-medium">
              {hasActiveFilters ? `${selectedStatuses.length || 1} filter(s) active` : 'No filter active'}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-2xs"
            >
              Done
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
